import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { useI18n } from "@/i18n";

export default function Privacy() {
  const { lang } = useI18n();
  const isEs = lang === "es";

  return (
    <div className="min-h-screen bg-obsidian text-gold-light">
      <header className="border-b border-gold-subtle bg-obsidian-2/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="privacy-back-home">
            <Logo />
          </Link>
          <Link to="/" className="text-xs text-faded hover:text-gold">
            ← Volver
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display text-4xl mb-2">Política de Privacidad</h1>
        <p className="text-xs text-faded mb-12">
          Última actualización: 2 de junio de 2026 · v1.1-2026-06-02
        </p>

        {!isEs && <LegalLangNotice lang={lang} />}

        {isEs && (
          <div className="space-y-8 text-sm leading-relaxed">
            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                1. Responsable del tratamiento
              </h2>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Nombre comercial:</strong> KeepSoul</li>
                <li><strong>Titular:</strong> Antonio Ferrer García</li>
                {/* PRIVACY-NIF-HIDDEN — restaurar NIF real o sustituir por CIF de SL antes de lanzamiento público */}
                <li>
                  <strong>Identificación fiscal:</strong> disponible bajo
                  solicitud a{" "}
                  <a
                    href="mailto:support@keepsoul.app"
                    className="text-gold hover:underline"
                  >
                    support@keepsoul.app
                  </a>
                </li>
                {/* PRIVACY-ADDRESS-HIDDEN — restaurar o sustituir por domicilio profesional antes de lanzamiento público */}
                <li>
                  <strong>Domicilio:</strong> Murcia, España (dirección postal
                  disponible bajo solicitud a{" "}
                  <a
                    href="mailto:support@keepsoul.app"
                    className="text-gold hover:underline"
                  >
                    support@keepsoul.app
                  </a>{" "}
                  para asuntos legales y notificaciones)
                </li>
                <li><strong>Forma jurídica:</strong> persona física (autónomo)</li>
                <li>
                  <strong>Email de contacto general:</strong>{" "}
                  <a
                    href="mailto:support@keepsoul.app"
                    className="text-gold hover:underline"
                  >
                    support@keepsoul.app
                  </a>
                </li>
                <li>
                  <strong>Delegado de Protección de Datos (DPO):</strong>{" "}
                  <a
                    href="mailto:dpo@keepsoul.app"
                    className="text-gold hover:underline"
                  >
                    dpo@keepsoul.app
                  </a>
                  {" "}— canal específico para asuntos de privacidad, RGPD, ejercicio de derechos y reporte de incidentes.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                2. Datos que recopilamos
              </h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Datos de cuenta:</strong> nombre y email. La
                  contraseña se almacena únicamente como hash bcrypt con sal
                  única (nunca en texto plano).
                </li>
                <li>
                  <strong>Contenido del Cofre:</strong> contraseñas,
                  documentos, semillas de cripto, IBAN y demás datos sensibles,
                  almacenados cifrados con AES-256-GCM en reposo.
                </li>
                <li>
                  <strong>Datos de herederos:</strong> nombre, email, teléfono
                  (opcional), relación con el usuario y porcentaje asignado del
                  legado.
                </li>
                <li>
                  <strong>Mensajes del Futuro y legado digital:</strong> texto,
                  audio, vídeo, imágenes y archivos programados para entrega
                  diferida, almacenados cifrados.
                </li>
                <li>
                  <strong>Registros de Fe de Vida:</strong> fechas de check-in,
                  intervalos configurados, escalado de notificaciones.
                </li>
                <li>
                  <strong>Datos de pago:</strong> gestionados íntegramente por
                  Stripe Payments Europe Ltd. (Irlanda). KeepSoul no almacena
                  números de tarjeta ni datos bancarios.
                </li>
                <li>
                  <strong>Datos técnicos:</strong> dirección IP, user-agent,
                  idioma del navegador y registros de inicio de sesión, con
                  fines de seguridad y auditoría.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                3. Base legal del tratamiento
              </h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Art. 6.1.a RGPD — Consentimiento:</strong> para el
                  envío de comunicaciones comerciales y para cookies no
                  estrictamente necesarias.
                </li>
                <li>
                  <strong>Art. 6.1.b RGPD — Ejecución de contrato:</strong>{" "}
                  para prestar el servicio contratado (Cofre, Mensajes del
                  Futuro, Fe de Vida, entrega del legado a herederos,
                  facturación).
                </li>
                <li>
                  <strong>Art. 6.1.c RGPD — Obligación legal:</strong> para el
                  cumplimiento de obligaciones contables, fiscales y de
                  seguridad informática.
                </li>
                <li>
                  <strong>Art. 6.1.f RGPD — Interés legítimo:</strong> para la
                  detección y prevención de fraude, abuso y ataques contra la
                  plataforma.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                4. Finalidades del tratamiento
              </h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Prestación del servicio de vault digital cifrado.</li>
                <li>
                  Sistema Fe de Vida y verificación periódica del estado del
                  usuario.
                </li>
                <li>
                  Entrega post-mortem del legado digital a los herederos
                  designados.
                </li>
                <li>Facturación, gestión de pagos y atención al cliente.</li>
                <li>
                  Cumplimiento de obligaciones legales y de seguridad
                  (auditoría, prevención de fraude).
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                5. Plazo de conservación (resumen)
              </h2>
              <p>
                Los datos personales del usuario se conservan{" "}
                <strong>durante toda la vida de la cuenta</strong>. Tras una
                solicitud de baja, los datos se eliminarán en un plazo máximo
                de <strong>30 días naturales</strong>. Los registros de
                auditoría (logs de seguridad) se conservarán anonimizados
                durante el plazo legalmente exigible para el cumplimiento de
                obligaciones de seguridad y prevención de fraude (Art. 30
                RGPD).
              </p>
              <p className="mt-3 text-faded text-xs">
                Consulta el desglose detallado por categoría en la{" "}
                <strong>sección 10</strong> de esta política.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                6. Cesión de datos a terceros
              </h2>
              <p className="mb-3">
                KeepSoul comparte únicamente los datos estrictamente necesarios
                con los siguientes encargados del tratamiento, todos ellos
                vinculados por contrato y por las garantías del RGPD:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Stripe Payments Europe Ltd.</strong> (Irlanda) — para
                  el procesamiento de pagos. Sujeto a su propio acuerdo de
                  tratamiento de datos.
                </li>
                <li>
                  <strong>Proveedor de hosting cloud (UE):</strong>{" "}
                  [pendiente de confirmar] — para el alojamiento de la base de
                  datos cifrada.
                </li>
                <li>
                  <strong>Resend</strong> (proveedor de email transaccional) —
                  para el envío de emails operativos. Resend solo recibe
                  metadatos del email (destinatario, asunto, plantilla); no
                  tiene acceso al contenido cifrado del legado.
                </li>
              </ul>
              <p className="mt-3 text-faded">
                <strong>
                  KeepSoul no vende ni cede datos personales con fines
                  comerciales a terceros.
                </strong>
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                7. Tus derechos (ARCO-POL · RGPD)
              </h2>
              <p className="mb-3">
                Puedes ejercer los derechos de acceso, rectificación, supresión
                (olvido), oposición, limitación, portabilidad y revocación del
                consentimiento por cualquiera de estas vías:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  Desde tu propia cuenta: Ajustes → "Exportar mis datos
                  (GDPR)" o "Eliminar mi cuenta".
                </li>
                <li>
                  Por email a{" "}
                  <a
                    href="mailto:support@keepsoul.app"
                    className="text-gold hover:underline"
                  >
                    support@keepsoul.app
                  </a>{" "}
                  desde la dirección registrada en tu cuenta. Para solicitudes
                  realizadas desde otra dirección, podremos requerir copia del
                  DNI/NIE u otro documento identificativo equivalente para
                  verificar tu identidad.
                </li>
              </ul>
              <p className="mt-3">
                Tienes derecho a presentar reclamación ante la{" "}
                <strong>
                  Agencia Española de Protección de Datos (AEPD)
                </strong>{" "}
                en{" "}
                <a
                  href="https://www.aepd.es"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gold hover:underline"
                >
                  www.aepd.es
                </a>{" "}
                si consideras que el tratamiento de tus datos vulnera el RGPD
                o la LOPDGDD.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                8. Cifrado y seguridad de los datos
              </h2>
              <p>
                KeepSoul cifra los datos sensibles del usuario (contenido del
                Cofre, mensajes futuros, datos de herederos) mediante AES-256-GCM
                en reposo. Los datos se almacenan cifrados en nuestra
                infraestructura y se descifran únicamente en los siguientes
                supuestos: (i) cuando el titular autenticado accede a su
                cuenta y solicita visualizarlos, o (ii) cuando, tras la
                activación del sistema Fe de Vida y verificación documental
                del fallecimiento, se entregan al heredero designado por el
                usuario. KeepSoul nunca accede al contenido del usuario para
                finalidades distintas a las descritas, ni cede dicho contenido
                a terceros con fines comerciales. Una migración futura a un
                modelo de cifrado de extremo a extremo está prevista en la
                hoja de ruta del producto.
              </p>
              <p className="mt-3">
                Medidas técnicas adicionales: bcrypt con sal única para
                contraseñas de acceso, cookies de sesión HttpOnly + Secure +
                SameSite, HTTPS/TLS extremo a extremo, cabeceras de seguridad
                CSP/HSTS/X-Frame-Options y auditoría completa de eventos
                sensibles (login, alta de heredero, exportaciones, cambio de
                contraseña).
              </p>
            </section>

            <section data-testid="privacy-breach-section">
              <h2 className="font-display text-2xl text-gold mb-3">
                9. Notificación de brechas de seguridad
              </h2>
              <p>
                En cumplimiento de los artículos <strong>33 y 34 del RGPD</strong>,
                KeepSoul se compromete a:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>
                  <strong>Notificar a la AEPD en un plazo máximo de 72 horas</strong>{" "}
                  desde el momento en que tengamos constancia de cualquier
                  violación de la seguridad de los datos personales que pueda
                  suponer un riesgo para los derechos y libertades de las
                  personas físicas afectadas.
                </li>
                <li>
                  <strong>Comunicar sin dilación indebida a los usuarios afectados</strong>{" "}
                  — vía email a la dirección registrada — cuando la brecha
                  entrañe un alto riesgo, describiendo la naturaleza del
                  incidente, las medidas adoptadas y las recomendaciones para
                  mitigar posibles consecuencias.
                </li>
                <li>
                  <strong>Documentar internamente toda incidencia</strong>{" "}
                  (incluso aquellas no notificables) en nuestro registro
                  interno de brechas, junto con su análisis y las medidas
                  correctivas aplicadas.
                </li>
                <li>
                  <strong>Mantener un canal de reporte</strong> en{" "}
                  <a href="mailto:dpo@keepsoul.app" className="text-gold hover:underline">
                    dpo@keepsoul.app
                  </a>{" "}
                  para que terceros (investigadores de seguridad, usuarios o
                  encargados del tratamiento) puedan comunicarnos cualquier
                  hallazgo de manera responsable.
                </li>
              </ul>
              <p className="mt-3 text-faded text-xs">
                A día de hoy, KeepSoul no ha sufrido ninguna brecha de seguridad
                notificable. Este compromiso es preventivo y se ejecutará
                conforme a los plazos legales en caso de producirse.
              </p>
            </section>

            <section data-testid="privacy-retention-section">
              <h2 className="font-display text-2xl text-gold mb-3">
                10. Períodos de conservación de los datos
              </h2>
              <p className="mb-4">
                KeepSoul aplica el principio de <strong>limitación del plazo de
                conservación</strong> (Art. 5.1.e RGPD): cada categoría de dato
                se conserva sólo durante el tiempo estrictamente necesario para
                la finalidad para la que fue recogido o, en su caso, durante el
                plazo legalmente exigido.
              </p>
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <table className="w-full text-xs sm:text-sm border-collapse" data-testid="privacy-retention-table">
                  <thead>
                    <tr className="border-b border-gold-subtle">
                      <th className="text-left py-2 px-3 font-label text-faded">Categoría</th>
                      <th className="text-left py-2 px-3 font-label text-faded">Plazo de conservación</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gold-subtle/40">
                    <tr>
                      <td className="py-2 px-3"><strong>Datos de cuenta activa</strong> (email, nombre, password hash)</td>
                      <td className="py-2 px-3">Mientras la cuenta esté activa</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3"><strong>Contenido del Cofre y mensajes</strong></td>
                      <td className="py-2 px-3">Mientras la cuenta esté activa o hasta su entrega al heredero designado</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3"><strong>Cuentas inactivas sin login y sin Fe de Vida</strong></td>
                      <td className="py-2 px-3">Aviso por email a los 18 meses · borrado tras 24 meses sin respuesta</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3"><strong>Audit log y registro de actividades</strong> (Art. 30)</td>
                      <td className="py-2 px-3">5 años desde el evento, anonimizado al borrar la cuenta</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3"><strong>Mensajes no entregados tras ejecución del legado</strong></td>
                      <td className="py-2 px-3">12 meses tras la entrega, después se borran</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3"><strong>Datos de facturación y pagos</strong></td>
                      <td className="py-2 px-3">Conforme al Art. 30 del Código de Comercio y normativa fiscal española: <strong>6 años</strong></td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3"><strong>Consentimientos otorgados</strong> (cookies, términos, privacidad)</td>
                      <td className="py-2 px-3">Mientras la cuenta esté activa + 3 años para acreditar su prestación</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3"><strong>Backups y copias de seguridad</strong></td>
                      <td className="py-2 px-3">Rotación máxima 30 días; el ejercicio del derecho al olvido se ejecuta también sobre los backups en el siguiente ciclo</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3"><strong>Tokens de export y enlaces firmados</strong></td>
                      <td className="py-2 px-3">60 minutos (uso único)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-faded text-xs">
                Pasados estos plazos, los datos se eliminan de forma irreversible
                o, cuando proceda por obligación legal, se anonimizan de manera
                que no sea posible identificar al usuario.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                11. Tratamiento post-mortem y entrega del legado
              </h2>
              <p className="mb-3">
                La finalidad principal de KeepSoul es entregar el legado
                digital del usuario a los herederos designados por éste tras
                la confirmación documental de su fallecimiento. El
                procedimiento es el siguiente:
              </p>
              <ol className="list-decimal pl-6 space-y-1">
                <li>
                  Activación del protocolo Fe de Vida: el usuario no responde
                  a las verificaciones periódicas en el intervalo configurado.
                </li>
                <li>
                  Notificación escalada a los contactos de confianza y, en su
                  caso, a los herederos designados.
                </li>
                <li>
                  Para acceder al legado, el heredero deberá aportar:{" "}
                  <strong>
                    (a) Certificado de defunción oficial del titular, (b) Copia
                    de su DNI/NIE o documento identificativo equivalente, y
                    (c) Cualquier documentación adicional que KeepSoul estime
                    razonablemente necesaria para verificar su identidad y
                    legitimación como heredero.
                  </strong>
                </li>
                <li>
                  Tras verificación documental, KeepSoul entregará al heredero
                  la parte del legado que le corresponda según las
                  instrucciones del titular.
                </li>
              </ol>
              <p className="mt-3 text-faded">
                Esta cláusula constituye la disposición del titular sobre el
                tratamiento de sus datos personales tras su fallecimiento,
                conforme al artículo 3 de la LOPDGDD 3/2018.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                12. Comunicaciones comerciales
              </h2>
              <p>
                Si has marcado la casilla de aceptación de comunicaciones
                comerciales en el registro, podremos enviarte información
                sobre novedades del producto y mejoras del servicio. Puedes
                revocar este consentimiento en cualquier momento desde Ajustes
                o respondiendo "BAJA" a cualquier email comercial.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                13. Cambios en esta política
              </h2>
              <p>
                Cualquier modificación material de esta política será
                notificada por email a los usuarios registrados con al menos
                30 días naturales de antelación. La versión vigente siempre
                estará accesible en esta URL.
              </p>
            </section>
          </div>
        )}

        <div className="mt-12 pt-6 border-t border-gold-subtle text-xs text-faded text-center space-x-3">
          <Link to="/legal/aviso-legal" className="hover:text-gold">
            Aviso Legal
          </Link>
          <span className="text-faded/40">·</span>
          <Link to="/legal/terminos" className="hover:text-gold">
            Términos
          </Link>
          <span className="text-faded/40">·</span>
          <Link to="/legal/cookies" className="hover:text-gold">
            Cookies
          </Link>
          <span className="text-faded/40">·</span>
          <Link to="/" className="hover:text-gold">
            Inicio
          </Link>
        </div>
      </main>
    </div>
  );
}

function LegalLangNotice({ lang }) {
  if (lang === "zh") {
    return (
      <div className="rounded border border-gold-subtle bg-obsidian-2/40 p-5 text-sm leading-relaxed">
        <p className="mb-2 text-gold-light">
          法律文件目前以西班牙语提供,西班牙语是依据西班牙消费者法律管辖本合同的语言。
        </p>
        <p className="text-faded">
          英文和中文译本即将提供。如需说明,请联系:{" "}
          <a
            href="mailto:support@keepsoul.app"
            className="text-gold hover:underline"
          >
            support@keepsoul.app
          </a>
        </p>
      </div>
    );
  }
  return (
    <div className="rounded border border-gold-subtle bg-obsidian-2/40 p-5 text-sm leading-relaxed">
      <p className="mb-2 text-gold-light">
        The legal documentation is currently available in Spanish, which is the
        language governing the contract under Spanish consumer law.
      </p>
      <p className="text-faded">
        An English translation will be available soon. For clarifications:{" "}
        <a
          href="mailto:support@keepsoul.app"
          className="text-gold hover:underline"
        >
          support@keepsoul.app
        </a>
      </p>
    </div>
  );
}
